// TODO: Add scroll animated video class here (from johan-st.js)

import { helpers } from "../helpers";

/**
 * @description ScrollAnimatedVideo is a class for animating a video based on the scroll position. It is build with `product-scroll-animated-video.tpl` in mind.
 *
 * change animation strategy to exponental smoothing. (ref: https://lisyarus.github.io/blog/posts/exponential-smoothing.html)
 *
 * formula: `position += (target - position) * (1 - exp(- speed * dt))`
 */
export class ScrollAnimatedVideo {


    /**
     * @param {HTMLElement} elementWrapper
     */
    constructor(elementWrapper) {
        // parameters
        this.pxPerSec = 333; // scroll distance per second video in pixels (px)
        this.smoothingTimeMs = 2_000; // time in milliseconds to animate to the target time
        this.videoFrameMs = 41.666; // 24 fps
        this.smoothing = true;

        // state
        this.scrollLength = 0;
        this.videoUpdateLatestMs = 0;
        this.videoCurrentTimeLatestSec = 0;
        this.videoTargetTimeSec = 0;
        this.animating = false;

        // elements
        this.elWrapper = null;
        this.elBox = null;
        this.elVideo = null;
        this.elHeader = null;

        this.elWrapper = elementWrapper;
        if (!this.elWrapper) {
            console.error("[ScrollAnimatedVideo/constructor] elementWrapper is missing")
            helpers.alertEmail({
                sender: "ScrollAnimatedVideo",
                subject: "ScrollAnimatedVideo/constructor - elementWrapper is missing",
                message: "elementWrapper is missing.",
            });
            return;
        }
        this.elBox = this.elWrapper.querySelector(".scroll-animated-video");
        if (!this.elBox) {
            console.error("[ScrollAnimatedVideo/constructor] no \"box\" found");
            helpers.alertEmail({
                sender: "ScrollAnimatedVideo",
                subject: "ScrollAnimatedVideo/constructor - no \"box\" found",
                message: "no \"box\" found.",
            });
            return;
        }
        this.elVideo = this.elWrapper.querySelector("video");
        if (!this.elVideo) {
            console.error("[ScrollAnimatedVideo/constructor] no video found");
            helpers.alertEmail({
                sender: "ScrollAnimatedVideo",
                subject: "ScrollAnimatedVideo/constructor - no video found",
                message: "no video found.",
            });
            return;
        }
        this.elHeader = document.querySelector("#main-header");
        if (!this.elHeader) {
            console.error("[ScrollAnimatedVideo/constructor] no header found");
            helpers.alertEmail({
                sender: "ScrollAnimatedVideo",
                subject: "ScrollAnimatedVideo/constructor - no header found",
                message: "no header found.",
            });
            return;
        }


        // video height, should be in sync with css.
        this.videoHeight = window.innerHeight - this.elHeader.offsetHeight;

        // Bind methods to ensure they maintain `this` context
        this.animate = this.animate.bind(this);
        this._frame = this._frame.bind(this);
        this._handleScroll = this._handleScroll.bind(this);

        if (!this.elVideo.duration) {
            // console.debug("[ScrollAnimatedVideo] video duration not available yet. listening for loadedmetadata event.");
            this.elVideo.addEventListener('loadedmetadata', () => {
                // console.debug('[ScrollAnimatedVideo] Duration is now available:', this.elVideo.duration);
                this._initData();
                this._setListeners();
                this._handleScroll();
            });
        } else {
            // console.debug("[ScrollAnimatedVideo] video was available:", this.elVideo.duration, "seconds.");
            this._initData();
            this._setListeners();
            this._handleScroll();
        }
    }


    /**
     *
     * @param {Number} targetTime
     * @returns void
     * @description animate the video to the target time.
     */
    animate(targetTime) {
        // console.debug("[ScrollAnimatedVideo] animate", targetTime);
        if (!this.smoothing) {
            // console.debug("[ScrollAnimatedVideo] animate - no smoothing");
            this.elVideo.currentTime = targetTime;
            // this._scrollText();
            this._fadeText();
            return;
        }

        if (this.animating) {
            // console.debug("[ScrollAnimatedVideo] animate - already animating");
            this.videoTargetTimeSec = targetTime;
            return;
        }

        this.videoUpdateLatestMs = performance.now();
        this.videoLatestSec = this.elVideo.currentTime;
        this.videoTargetTimeSec = targetTime;

        /** /
        console.debug("[ScrollAnimatedVideo] animate",
            "\nto", targetTime,
            "\nfrom", this.elVideo.currentTime,
            "\nnowMs:", this.videoUpdateLatestMs);
        /**/

        this._frame(this.videoUpdateLatestMs);
        requestAnimationFrame(this._frame);
    }

    /**
     * @param {Number} nowMs
     * @returns void
     * @description frame function for the animation. This function is called recursively until the target time is reached.
     */
    _frame(nowMs) {
        this.animating = true;
        const dt = nowMs - this.videoUpdateLatestMs; // delta time - time since last frame
        const videoDiffSec = this.videoTargetTimeSec - this.elVideo.currentTime; // difference between current and target time
        /** determine fraction of the remaining time to move.
        * This is used to smooth the animation.
        * The fraction can be no smaller than the given frame time.
        * (if the entire smoothing time is smaller than the frame time, the video will be set to the target time.)
        */
        const stepFraction = Math.min(this.videoFrameMs, dt / this.smoothingTimeMs);
        const videoNewTimeSec = this.elVideo.currentTime + videoDiffSec * stepFraction; // calculate new time based on the fraction

        /** /
        console.debug("[ScrollAnimatedVideo] frame",
            "\ndt", dt,
            "\nfrom", this.elVideo.currentTime,
            "\nto", videoNewTimeSec,
            "\ndiff", videoDiffSec,
            "\nnowMs:", this.videoUpdateLatestMs);
        /**/

        // if the difference between the current time is smaller than the frame time, the video is set to the target time and the animation stops.
        if (Math.abs(videoDiffSec * 1000) < this.videoFrameMs) {
            this.elVideo.currentTime = this.videoTargetTimeSec;
            // this._scrollText();
            this._fadeText();
            this.animating = false;
            return;
        }

        this.elVideo.currentTime = videoNewTimeSec;
        // this._scrollText();
        this._fadeText();
        requestAnimationFrame(this._frame);
    }



    _initData() {
        // console.debug("[ScrollAnimatedVideo] initDataAttributes");
        const wrap = this.elWrapper;
        // parameters
        if (wrap.dataset.pxPerSec && !isNaN(Number(wrap.dataset.pxPerSec))) this.pxPerSec = Number(wrap.dataset.pxPerSec);
        if (wrap.dataset.smoothingTimeMs && !isNaN(Number(wrap.dataset.smoothingTimeMs))) this.smoothingTimeMs = Number(wrap.dataset.smoothingTimeMs);
        if (wrap.dataset.videoFrameMs && !isNaN(Number(wrap.dataset.videoFrameMs))) this.videoFrameMs = Number(wrap.dataset.videoFrameMs);
        if (wrap.dataset.smoothing && wrap.dataset.smoothing === "false") this.smoothing = false;

        wrap.dataset.pxPerSec = this.pxPerSec;
        wrap.dataset.smoothingTimeMs = this.smoothingTimeMs;
        wrap.dataset.videoFrameMs = this.videoFrameMs;
        wrap.dataset.smoothing = this.smoothing;

        this.scrollLength = this.elVideo.duration * this.pxPerSec;
        /** /
        console.log("[ScrollAnimatedVideo] initDataAttributes",
            "\npxPerSec", this.pxPerSec,
            "\nsmoothingTimeMs", this.smoothingTimeMs,
            "\nvideoFrameMs", this.videoFrameMs,
            "\nsmoothing", this.smoothing,
            "\nscrollLength", this.scrollLength,
            "\nvideoDuration", this.elVideo.duration,
            "\nvideoHeight", this.videoHeight,
            "\nwrapperHeight", this.scrollLength + this.videoHeight,
        );
        /**/
        wrap.style.height = `${this.scrollLength + this.videoHeight}px`;

        wrap.classList.add("scroll-animated-video__wrapper--initialized");
        wrap.style.setProperty("--header-height", `${this.elHeader.offsetHeight}px`);
    }

    _setListeners() {
        // console.debug("[ScrollAnimatedVideo] setListeners");
        if (!this.elVideo.duration) {
            throw new Error("[ScrollAnimatedVideo] falsy video duration");
        }
        document.addEventListener("scroll", helpers.throttle(this._handleScroll, this.videoFrameMs));
    }

    _handleScroll() {
        const offsetStart = this.elWrapper.offsetTop + this.elHeader.offsetHeight;
        const offsetStop = offsetStart + this.scrollLength;
        this.elWrapper.style.setProperty("--header-height", `${this.elHeader.offsetHeight}px`);

        /** /
        console.log("[ScrollAnimatedVideo] handleScroll",
            "\noffsetTop", offsetTop,
            "\noffsetBottom", offsetBottom,
            "\nscrollY", window.scrollY,
        )
        /**/

        // above
        if (scrollY < offsetStart) {
            // console.debug("[ScrollAnimatedVideo] handleScroll - above");
            this.elWrapper.classList.remove("sticky");
            this.elWrapper.classList.remove("bottom");
            if (this.elVideo.currentTime !== 0) {
                this.animate(0);
            }
            return;
        }
        //under
        if (scrollY > offsetStop) {
            // console.debug("[ScrollAnimatedVideo] handleScroll - under");
            this.elWrapper.classList.remove("sticky");
            this.elWrapper.classList.add("bottom");
            if (this.elVideo.currentTime !== this.elVideo.duration) {
                this.animate(this.elVideo.duration);
            }
            return;
        }
        // on
        // console.debug("[ScrollAnimatedVideo] handleScroll - on");
        this.elWrapper.classList.add("sticky");
        this.elWrapper.classList.remove("bottom");
        const targetTime = ((scrollY - offsetStart) / this.scrollLength) * this.elVideo.duration;
        // console.debug("[ScrollAnimatedVideo] handleScroll - on - targetTime", targetTime)
        this.animate(targetTime);
    }

    _scrollText() {
        const videoHeight = this.elVideo.offsetHeight;
        const text = this.elBox.querySelector(".scroll-animated-video__text");
        const textHeight = text.offsetHeight;
        const textMinPx = 0;
        const textTravelPx = videoHeight - textHeight;
        const textNewTopPx = textMinPx + textTravelPx - (textTravelPx * (this.elVideo.currentTime / this.elVideo.duration));
        text.style.top = `${textNewTopPx}px`
    }

    _fadeText() {
        // handler
        function handleEl(el, timeCurrent) {
            const inFrom = Number(el.dataset.fadeInFrom)
            const inTo = Number(el.dataset.fadeInTo)
            const outFrom = Number(el.dataset.fadeOutFrom)
            const outTo = Number(el.dataset.fadeOutTo)
            if (isNaN(inFrom) ||
                isNaN(inTo) ||
                isNaN(outFrom) ||
                isNaN(outTo)) {
                console.error("[ScrollAnimatedVideo] _fadeText/handleEl - invalid data attributes")
                return
            }
            if (inFrom <= timeCurrent && timeCurrent <= inTo) {
                el.style.opacity = (timeCurrent - inFrom) / (inTo - inFrom)
            } else if (inTo <= timeCurrent && timeCurrent <= outFrom) {
                el.style.opacity = 1
            } else if (outFrom <= timeCurrent && timeCurrent <= outTo) {
                el.style.opacity = 1 - (timeCurrent - outFrom) / (outTo - outFrom)
            } else {
                el.style.opacity = 0
            }
        }

        // loop
        const els = this.elBox.querySelectorAll(".scroll-animated-video__text>*");
        els.forEach(el => {
            handleEl(el, this.elVideo.currentTime)
        })
    }

}
